import { type FC, type ReactNode } from "react";
import { Box, Typography } from "~/common/mui";

export type ParameterProps = JSX.IntrinsicElements["div"] & {
  label: ReactNode;
  value?: ReactNode;
  variant?: "vertical" | "horizontal" | "search-page";
  cutOverflow?: boolean;
  "data-test-id"?: string;
};

const Parameter: FC<ParameterProps> = (props) => {
  const {
    label,
    value,
    className,
    children,
    "data-test-id": testId,
    variant = "vertical",
    cutOverflow,
    ...divProps
  } = props;

  const isVertical = variant === "vertical";
  const labelVariant = isVertical ? "bodySmall" : "bodyMedium";
  const valueVariant = isVertical ? "bodyLarge" : "bodyMedium";

  const rootSx: Record<string, unknown> = {};
  if (variant === "horizontal") rootSx.display = "flex";
  if (variant === "search-page") rootSx.display = "contents";
  if (cutOverflow) rootSx.width = "100%";

  return (
    <div {...divProps} className={className} style={rootSx.display || rootSx.width ? { ...rootSx } : undefined}>
      <Typography variant={labelVariant} component="div" sx={{ color: "text.label" }}>
        {label}
      </Typography>
      <Typography
        variant={valueVariant}
        component="div"
        data-test-id={testId}
        sx={{
          color: "text.primary",
          ...((variant === "horizontal" || variant === "search-page") ? { mt: 0, ml: variant === "horizontal" ? 0.5 : 0 } : { mt: 0.5 }),
          ...(cutOverflow && { whiteSpace: "pre", textOverflow: "ellipsis", overflow: "hidden" }),
        }}
      >
        {children || value}
      </Typography>
    </div>
  );
};

export default Parameter;

interface ReverseParamProps {
  label: ReactNode;
  children?: ReactNode;
}
export const ReverseParam = ({ label, children }: ReverseParamProps) => {
  return (
    <div>
      <div className="mb-1">{label}</div>
      {children && <div className="f-label-color">{children}</div>}
    </div>
  );
};
